import { useQuery } from '@tanstack/react-query';
import { Spin } from 'antd';
import { ItemType } from 'antd/es/menu/interface';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { authApi } from '../../apis';
import { UserRoleEnum } from '../../apis/client-axios';
import { ADMIN_PROFILE } from '../../constants/QueryKey';
import { ADMIN_ROUTE_NAME } from '../../constants/RouteName';
import { STORAGE } from '../../constants/Storage';
import MainApp from '../../containers/App/MainApp';
import { getMenuActiveIconName, useAdminMenuItems } from './Menus';
import { useDispatch } from 'react-redux';
import { updateMe } from '../../store/authSlice';

export const configurationManagementRoutes = [
  ADMIN_ROUTE_NAME.LOGO,
  ADMIN_ROUTE_NAME.WORKING_SCHEDULE,
  ADMIN_ROUTE_NAME.WORKING_HOURS,
  ADMIN_ROUTE_NAME.OVERTIME_HOURS,
  ADMIN_ROUTE_NAME.REQUEST_REVIEW,
  ADMIN_ROUTE_NAME.SETTING_MESSAGE,
  ADMIN_ROUTE_NAME.HOLIDAY,
  ADMIN_ROUTE_NAME.CHOICE_SETTINGS,
  ADMIN_ROUTE_NAME.ATTENDANCE_CONFIRMATION,
  ADMIN_ROUTE_NAME.TIMEKEEPING_REMINDER,
];

export const staffDepartmentPosition = [
  ADMIN_ROUTE_NAME.STAFF_TYPE_MANAGEMENT,
  ADMIN_ROUTE_NAME.POSITION_MANAGEMENT,
  ADMIN_ROUTE_NAME.DEPARTMENT_MANAGEMENT,
];

export const locationManagementRoutes = [ADMIN_ROUTE_NAME.LOCATION, ADMIN_ROUTE_NAME.LOCATION_LIST];

export const termsOfUseAndPolicies = [ADMIN_ROUTE_NAME.PRIVACY_POLICY, ADMIN_ROUTE_NAME.TERMS_OF_SERVICE];

const Admin = () => {
  const location = useLocation();

  const dispatch = useDispatch();

  const { data, isLoading } = useQuery({
    queryKey: [ADMIN_PROFILE],
    queryFn: () => authApi.authControllerProfile(),
    enabled: !!localStorage.getItem(STORAGE.TOKEN),
    staleTime: 10000,
  });

  const adminMenuItems = useAdminMenuItems({ useRole: data?.data?.data?.role as string }) as ItemType[];

  useEffect(() => {
    if (data?.data?.data) {
      dispatch(updateMe(data?.data?.data));
    }
  }, [dispatch, data?.data?.data]);

  const filterMenuByPermission = useCallback(
    (menuItems: ItemType[]) => {
      const userRole = data?.data?.data?.role;

      if (!userRole) {
        return [];
      }

      let filteredItems: ItemType[] = [];

      if (userRole === UserRoleEnum.SuperAdmin) {
        filteredItems = menuItems.filter(
          (item) =>
            item?.type === 'divider' ||
            (item?.key &&
              (item.key === ADMIN_ROUTE_NAME.STAFF_MANAGEMENT ||
                item.key === ADMIN_ROUTE_NAME.STAFF_DEPARTMENT_POSITION ||
                item.key === ADMIN_ROUTE_NAME.SCHEDULE ||
                item.key === ADMIN_ROUTE_NAME.BOOKING ||
                item.key === ADMIN_ROUTE_NAME.LOCATION_MANAGEMENT ||
                item.key === ADMIN_ROUTE_NAME.CONFIGURATION_MANAGEMENT ||
                item.key === ADMIN_ROUTE_NAME.AFFILIATE_ACCOUNT)),
        );
      } else if (userRole === UserRoleEnum.Employee) {
        filteredItems = menuItems.filter(
          (item) =>
            item?.type === 'divider' ||
            (item?.key &&
              (item.key === ADMIN_ROUTE_NAME.EMPLOYEE_ATTENDANCE ||
                item.key === ADMIN_ROUTE_NAME.EMPLOYEE_APPLICAIION ||
                item.key === ADMIN_ROUTE_NAME.EMPLOYEE_CONFIRM)),
        );
      } else if (userRole === UserRoleEnum.Manager) {
        filteredItems = menuItems.filter(
          (item) =>
            item?.type === 'divider' ||
            (item?.key &&
              (item.key === ADMIN_ROUTE_NAME.ATTENDANCE_MANAGEMENT ||
                item.key === ADMIN_ROUTE_NAME.TIMEKEEPING_CONFIRMATION ||
                item.key === ADMIN_ROUTE_NAME.APPLY_FOR_RECOGNITION ||
                item.key === ADMIN_ROUTE_NAME.OUT_OF_YEAR_MANAGEMENT ||
                item.key === ADMIN_ROUTE_NAME.ANNUAL_CONFIRMATION)),
        );
      } else {
        filteredItems = menuItems.filter((item) => item?.type !== 'divider' && item?.key);
      }

      return filteredItems.concat(
        menuItems.filter(
          (item) => item?.type !== 'divider' && item?.key === ADMIN_ROUTE_NAME.TERMS_OF_USE_AND_POLICIES,
        ),
      );
    },
    [data],
  );

  const handleChangeIconMenu = useCallback((menuItems: ItemType[], pathName: string) => {
    return menuItems
      .map((item: ItemType) => {
        if (
          pathName.includes(item?.key as string) ||
          (item?.key === ADMIN_ROUTE_NAME.CONFIGURATION_MANAGEMENT &&
            configurationManagementRoutes.some((configPath) => pathName.includes(configPath))) ||
          (item?.key === ADMIN_ROUTE_NAME.STAFF_DEPARTMENT_POSITION &&
            staffDepartmentPosition.some((configPath) => pathName.includes(configPath))) ||
          (item?.key === ADMIN_ROUTE_NAME.LOCATION_MANAGEMENT &&
            locationManagementRoutes.some((configPath) => pathName.includes(configPath))) ||
          (item?.key === ADMIN_ROUTE_NAME.TERMS_OF_USE_AND_POLICIES &&
            termsOfUseAndPolicies.some((configPath) => pathName.includes(configPath)))
        ) {
          return {
            ...item,
            icon: <div className="menu-icon-small">{getMenuActiveIconName(item?.key as string)}</div>,
          };
        }
        return item;
      })
      .filter((item): item is ItemType => item !== null);
  }, []);

  const menu = useMemo(() => {
    if (!data) return [];
    const filteredMenu = filterMenuByPermission(adminMenuItems);
    return handleChangeIconMenu(filteredMenu, location.pathname);
  }, [data, adminMenuItems, filterMenuByPermission, handleChangeIconMenu, location.pathname]);

  if (isLoading) {
    return (
      <div>
        <div className="d-flex justify-content-center align-items-center vh-100 text-center">
          <Spin />
        </div>
      </div>
    );
  }

  return <MainApp menuItems={menu} />;
};

export default Admin;
